import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatDetail } from "@/lib/api";
import { toast } from "sonner";
import BrandMark from "@/components/BrandMark";

export default function Login() {
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [tfaCode, setTfaCode] = useState("");
  const [tfaMode, setTfaMode] = useState(false); // triggered when server responds 428
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [forgotBusy, setForgotBusy] = useState(false);
  const { login, user } = useAuth();
  const submit = async (e) => {
    e.preventDefault(); setErr(""); setLoading(true);
    try {
      const u = await login(email, password, tfaMode ? tfaCode : undefined);
      const home = (u?.role || user?.role) === "agent" ? "/app/entry" : "/app";
      nav(home);
    }
    catch (e2) {
      if (e2.response?.status === 428) { setTfaMode(true); setErr(""); }
      else { setErr(formatDetail(e2.response?.data?.detail) || e2.message); }
    }
    finally { setLoading(false); }
  };
  const forgot = async () => {
    if (!email) return toast.error("Enter your email above first");
    setForgotBusy(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email });
      toast.success(data.message || "If an account exists for that email, a reset link is on its way.");
    } catch (e) { toast.error(formatDetail(e.response?.data?.detail) || "Could not send reset link"); }
    finally { setForgotBusy(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#FAFAFA] text-zinc-900">
      <div className="hidden lg:block relative overflow-hidden border-r border-black/5">
        <img src="https://images.unsplash.com/photo-1581092795360-fd1ca04f0952?crop=entropy&cs=srgb&fm=jpg&w=1400"
             alt="" className="absolute inset-0 w-full h-full object-cover opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#FAFAFA] via-[#0A0A0A]/60 to-transparent" />
        <div className="relative h-full flex flex-col justify-end p-12">
          <div className="flex items-center gap-2 mb-4">
            <span className="live-dot" />
            <span className="text-xs uppercase tracking-[0.3em] text-zinc-300">Operations Center</span>
          </div>
          <h1 className="font-display text-5xl xl:text-6xl leading-[0.9]">
            Every ballot.<br/>Every ward.<br/>Verified in real-time.
          </h1>
          <p className="text-sm text-zinc-400 mt-6 max-w-md">
            Secure collation of polling-unit results with dual-verification workflow, hierarchy aggregation and full audit trail.
          </p>
        </div>
      </div>

      <div className="flex items-center justify-center p-8">
        <form onSubmit={submit} className="w-full max-w-sm" data-testid="login-form">
          <Link to="/" className="inline-block mb-10">
            <BrandMark variant="lockup" size={64} />
          </Link>
          <div className="text-xs uppercase tracking-[0.3em] text-zinc-500">Console</div>
          <h2 className="font-display text-4xl mt-2 mb-8">Sign in</h2>

          <label className="text-xs uppercase tracking-[0.2em] text-zinc-500">Email</label>
          <input value={email} onChange={(e) => setEmail(e.target.value)} type="email" required autoFocus
            data-testid="login-email-input"
            className="w-full bg-transparent border-b border-black/20 py-3 focus:border-black outline-none text-lg mb-6" />

          <label className="text-xs uppercase tracking-[0.2em] text-zinc-500">Password</label>
          <input value={password} onChange={(e) => setPassword(e.target.value)} type="password" required
            data-testid="login-password-input"
            className="w-full bg-transparent border-b border-black/20 py-3 focus:border-black outline-none text-lg mb-2" />

          <button type="button" onClick={forgot} disabled={forgotBusy} data-testid="forgot-password-link"
            className="text-[11px] uppercase tracking-[0.15em] text-zinc-500 hover:text-black underline underline-offset-4 mb-6 disabled:opacity-50">
            {forgotBusy ? "Sending…" : "Forgot password?"}
          </button>

          {tfaMode && (
            <div className="mb-6" data-testid="tfa-block">
              <label className="text-xs uppercase tracking-[0.2em] text-emerald-700">Two-factor code</label>
              <input value={tfaCode} onChange={e=>setTfaCode(e.target.value)} required autoFocus
              data-testid="login-tfa-input" inputMode="text" maxLength={12}
                className="w-full bg-transparent border-b-2 border-emerald-500 py-3 focus:border-emerald-700 outline-none text-2xl tabular tracking-widest"/>
              <div className="text-[11px] text-zinc-500 mt-1">Enter the 6-digit code from your authenticator app — or one of your 8-character backup codes.</div>
            </div>
          )}

          {err && <div className="text-sm text-red-500 mb-4" data-testid="login-error">{err}</div>}

          <button type="submit" disabled={loading} data-testid="login-submit-btn"
            className="w-full py-4 bg-black text-white font-semibold uppercase tracking-[0.2em] text-sm hover:bg-zinc-800 transition-colors disabled:opacity-50">
            {loading ? "Authenticating..." : tfaMode ? "Verify & Sign in" : "Access Console"}
          </button>

          <div className="mt-8 text-xs text-zinc-500">
            <div>Public results — no login required.</div>
            <Link to="/" className="underline underline-offset-4 hover:text-black transition-colors">← Back to live dashboard</Link>
          </div>
        </form>
      </div>
    </div>
  );
}
